import {
  EvaluationContainer,
  EvaluationFooter,
  QuestionsCarousel,
} from "@components/evaluation";
import EvaluationMainContent from "@components/evaluationmaincontent/EvaluationMainContent";
import EvaluationPageHeader from "@components/evaluationpageheader/EvaluationPageHeader";
import ReportSummary from "@components/reportsummary/ReportSummary";
import AssignNextTaskConfirmation from "@modals/assignnexttaskconfirmation/AssignNextTaskConfirmation";
import {
  saveQuestionEvaluation,
  submitTaskEvaluation,
} from "@services/evaluationService";
import { setComponentToShow } from "@store/ModalToShow";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import styles from "./EvaluatorPage.module.css";
import type { EvaluationFormData } from "./QuestionWise";
import ViewShortcuts from "@modals/viewshortcuts/ViewShortcuts";
import { useShortcut } from "@hooks/useShortCut";

interface AnswersheetWiseProps {
  examSessionData: any;
  sessionName:string;
  handleAssignNextTask: () => Promise<void>;
  // onApproveAndNext?: () => Promise<void>;
  onEvaluationDataChange: (data: EvaluationFormData) => void;
}

function AnswersheetWise({ examSessionData, sessionName, handleAssignNextTask }: AnswersheetWiseProps) {
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [report, setReport] = useState(false);
  const [approvalValid, setApprovalValid] = useState(true);
  const [evaluationData, setEvaluationData] = useState<EvaluationFormData>({
    marksAwarded: 0,
    feedback: "",
    verdict: "",
  });
  const [approvedQuestions, setApprovedQuestions] = useState<Set<number>>(new Set());
  const [isSaving,setIsSaving]=useState(false);
  const accesstoken = localStorage.getItem("accessToken");
  const navigate=useNavigate();
  const dispatch=useDispatch();
  const componentName = useSelector((state: any) => state.modalToShow.componentName);
  useShortcut("alt+/", () => {
          dispatch(setComponentToShow("viewShortcuts"));
        });
  const handleFormDataChange = useCallback((data: any, variant: any) => {
    console.log("answersheetwise - handleFormDataChange called:", {
      data,
      variant,
    });

    // Handle both number and string values for marks
    const marksValue = data.marksAwarded || data.marks || 0;
    const marksAwarded =
      typeof marksValue === "string" ? parseFloat(marksValue) || 0 : marksValue;

    const newEvaluationData = {
      marksAwarded: marksAwarded,
      feedback: data.feedback || data.remarks || "",
      verdict: data.verdict || data.evaluation_verdict || "",
    };

    setEvaluationData(newEvaluationData);
  }, []);

  const hasEvaluationDataChanged = useCallback(() => {
    if (!currentQuestion?.current_evaluation) return false;

    const currentEval = currentQuestion.current_evaluation;

    const currentMarks = Number(currentEval.awarded_marks);
    const formMarks = Number(evaluationData.marksAwarded);

    if (currentMarks !== formMarks) return true;

    const currentFeedback = (currentEval.feedbacks ?? [])
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

    const formFeedback = (evaluationData.feedback ?? "")
      .replace(/\s+/g, " ")
      .trim();

    if (currentFeedback !== formFeedback) return true;

    if (currentEval.evaluation_verdict !== evaluationData.verdict) return true;

    return false;
  }, [currentQuestion, evaluationData]);
  

  const handleApproveAndNext = async () => {
    if (!currentQuestion) return;

    // Previously evaluated, but data changed
    if (currentQuestion.current_evaluation) {
      if (hasEvaluationDataChanged()) {
        await saveEvaluation();
      }
      goToNextQuestion();
      return;
    }

    // First-time evaluation
    await saveEvaluation();
  };

  const saveEvaluation = async () => {
    console.log(evaluationData);
    setIsSaving(true);
    try {
      if (!currentQuestion) {
        throw new Error("No evaluation task or question available");
      }
      // const questionWithEvaluation = currentQuestion.questions[questionIndex];
      const feedbacks = evaluationData.feedback
        ? [evaluationData.feedback]
        : [];
      await saveQuestionEvaluation({
        taskId: examSessionData.task_id,
        questionId: currentQuestion.question.id,
        awardedMarks: evaluationData.marksAwarded,
        feedbacks: feedbacks,
        verdict: evaluationData.verdict,
        token: accesstoken ?? "",
      });
      console.log("Question evaluation saved successfully");
      console.log(evaluationData);
      
      // Update the current_evaluation in local state
      const updatedQuestions = questions.map((q, index) => {
        if (index === questionIndex) {
          return {
            ...q,
            current_evaluation: {
              awarded_marks: evaluationData.marksAwarded,
              feedbacks: feedbacks,
              evaluation_verdict: evaluationData.verdict
            }
          };
        }
        return q;
      });
      setQuestions(updatedQuestions);
      
      // Update current question to reflect the changes immediately
      setCurrentQuestion(updatedQuestions[questionIndex]);
      
      const currentIndex = questionIndex;
      setApprovedQuestions((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentIndex);
        console.log("Updated Approved Set:", Array.from(newSet)); // Debugging line
        return newSet;
      });
      goToNextQuestion();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Error saving evaluation: " + errorMessage);
      console.error("Error in proceedWithSubmission:", err);
      throw err; // Re-throw to handle in calling functions
    } finally {
      setIsSaving(false);
    }
  };

  const proceedWithTaskSubmission = async () => {
    try {
      if (!examSessionData) {
        throw new Error("No evaluation task available");
      }
      // Close modal first
      dispatch(setComponentToShow(null));
      // Step 2: Submit the entire task
      // console.log("evaluationdata",evaluationData);
      // await saveEvaluation();
      await submitTaskEvaluation({
        taskId: examSessionData.task_id,
        token: accesstoken ?? "",
      });

      console.log("Task evaluation submitted successfully");
      navigate("/evaluator-dashboard");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Error submitting task: " + errorMessage);
      console.error("Error in proceedWithTaskSubmission:", err);
      throw err;
    }
  };

  // useEffect(() => {
  //   if (!examSessionData?.questions?.length) return;
  
  //   const sortedQuestions = [...examSessionData.questions].sort(
  //     (a, b) =>
  //       (a.question?.sequence_number ?? 0) -
  //       (b.question?.sequence_number ?? 0)
  //   );
  
  //   setQuestions(sortedQuestions);
  //   // PRE-FILL approvedQuestions state on initial load
  // const initialApproved = new Set<number>();
  // sortedQuestions.forEach((q, index) => {
  //   if (q.current_evaluation) {
  //     initialApproved.add(index);
  //   }
  // });
  // setApprovedQuestions(initialApproved);
  //   //to prevent out of bound index
  //   const safeIndex = Math.min(questionIndex, sortedQuestions.length - 1);
  //   const question = sortedQuestions[safeIndex];
  //   setQuestionIndex(safeIndex);
  //   setCurrentQuestion(question);
  
  //   const hasStudentComment =
  //     question?.student_comment && question.student_comment.length > 0;
  //   const hasPreviousEvaluation =
  //     question?.previous_evaluation_result !== null;
  
  //   setApprovalValid(
  //     (hasPreviousEvaluation && !hasStudentComment) ||
  //       question?.current_evaluation !== null
  //   );

  //   const pages = question?.page_mapping;
  // if (Array.isArray(pages) && pages.length > 0) {
  //   toast.dismiss(); // Clear old toasts so they don't stack up
    
  //   toast.info(
  //     `This question appears on multiple pages: ${pages.join(", ")}`,
  //     {
  //       autoClose: 3000,
  //       hideProgressBar: false,
  //       closeOnClick: true,
  //       pauseOnHover: true,
  //       draggable: true,
  //       // Use a unique ID to ensure the library treats this as a new event
  //       toastId: `q-${question.question.id}-${Date.now()}` 
  //     }
  //   );
  // }
  // }, [examSessionData, questionIndex]);

useEffect(() => {
  if (!examSessionData?.questions?.length) return;

  const sortedQuestions = [...examSessionData.questions].sort(
    (a, b) => (a.question?.sequence_number ?? 0) - (b.question?.sequence_number ?? 0)
  );
  setQuestions(sortedQuestions);

  // Sync approved state only when examSessionData changes (initial load)
  const initialApproved = new Set<number>();
  sortedQuestions.forEach((q, index) => {
    if (q.current_evaluation) {
      initialApproved.add(index);
    }
  });
  setApprovedQuestions(initialApproved);
}, [examSessionData]); 

useEffect(() => {
  if (questions.length === 0) return;

  const question = questions[questionIndex];
  if (!question) return;

  setCurrentQuestion(question);
  // Check if this question is already approved OR has current evaluation
  setApprovalValid(approvedQuestions.has(questionIndex) || question?.current_evaluation !== null);
  const pages = question?.page_mapping;
  if (Array.isArray(pages) && pages.length > 1) {
    toast.dismiss();
    toast.info(`Answer on pages: ${pages.join(", ")}`, { toastId: `q-${question.question.id}` });
  }
}, [questionIndex, questions]);

  const handleQuestionSelect = (index: number) => {
    toast.dismiss();
    if (index === -1) {
      setReport(true);
      return;
    }
    if (index < 0 || index >= questions.length) return; // boundary check
    setQuestionIndex(index);
    setCurrentQuestion(questions[index]);
    setReport(false);
  };

  const goToPrevQuestion = () => {
    handleQuestionSelect(questionIndex - 1);
  };

  const goToNextQuestion = () => {
    handleQuestionSelect(questionIndex + 1);
  };
  const handleApproveAndSubmit = async () => {
    if (!currentQuestion) return;

    try {
      // If question already has current_evaluation, no need to save
      if (!currentQuestion.current_evaluation) {
        await saveEvaluation();
      }
      window.location.href = "/evaluator-dashboard";
    } catch (err) {
      // Error already handled in the individual functions
      // Don't redirect if there was an error
    }
  };
  
  const saveAndshowConfirmationModal = async () => {
    // If question already has current_evaluation, check if data has been overridden
      await saveEvaluation();
    dispatch(setComponentToShow("AssignNextTaskConfirmation"));
  }
  const handleCloseModal = () => {
    dispatch(setComponentToShow(null));
  }

  const renderModal = () => {
    switch (componentName) {
      case "AssignNextTaskConfirmation":
        return (
          <AssignNextTaskConfirmation
            isOpen={true}
            onAssignNextTask={handleAssignNextTask}
            onSubmitAndGoBack={proceedWithTaskSubmission}
            onClose={handleCloseModal}
          />
        );
      case "viewShortcuts":
        return (
          <ViewShortcuts
            isOpen={true}
            onClose={() => dispatch(setComponentToShow(""))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.evaluationContainer}>
      <EvaluationPageHeader admissionID={examSessionData.exam_submission.submitted_by} 
            examSessionName={sessionName}
            onViewShortcutsClick={() => dispatch(setComponentToShow("ViewShortcuts"))}/>
      <div className={styles.mainContainer}>
        <QuestionsCarousel
          questions={questions}
          questionIndex={report ? -1 : questionIndex}
          onQuestionSelect={handleQuestionSelect}
          approvedQuestions={approvedQuestions}
        />
        {report ? (
          <ReportSummary questions={examSessionData.questions} />
        ) : (
          <div className={styles.evaluatorMainContent}>
            <EvaluationMainContent
              question={currentQuestion}
              studentAnswerDocument={
                examSessionData.exam_submission
                  .question_paper_submissions_documents[0]?.document
              }
            />
            <div className={styles.rightSidePanel}>
              {currentQuestion && (
                <EvaluationContainer
                  question={currentQuestion}
                  onApprovalValidityChange={setApprovalValid}
                  onFormDataChange={handleFormDataChange}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {!report && (
        <EvaluationFooter
          questionIndex={questionIndex}
          totalQuestions={questions.length}
          approvalValid={approvalValid}
          onPrevious={goToPrevQuestion}
          onApproveAndSubmit={saveAndshowConfirmationModal}
          onApproveAndNext={handleApproveAndNext}
          isSaving={isSaving}
        />
      )}
      <ToastContainer position="top-center" />
      {renderModal()}
    </div>
  );
}

export default AnswersheetWise;
